import { ref, type Ref } from "vue";
import { useToast } from "primevue/usetoast";
import { adminV1InviteUsersCreate } from "@/api/admin/admin";
import type {
  AdminProfile,
  AdminUserInviteRequest,
  AdminUserInviteResponse,
  AdminUserInviteResult,
} from "@/api/model";

/**
 * Extracts a user-friendly error message from API errors.
 * Handles the common error response shapes from the TFA API.
 */
const extractApiErrorMessage = (
  error: unknown,
  fallbackMessage: string = "An error occurred"
): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (!error) {
    return fallbackMessage;
  }

  const err = error as any;
  const errorData = err.response?.data || err.data;

  if (!errorData) {
    return err.message || fallbackMessage;
  }

  if (typeof errorData === "string") {
    return errorData;
  }

  // Check common error fields
  if (errorData.detail) return String(errorData.detail);
  if (errorData.error) return String(errorData.error);
  if (errorData.message) return String(errorData.message);

  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors.join(", ")
      : String(errorData.non_field_errors);
  }

  return fallbackMessage;
};

export interface UseBulkUserInviteReturn {
  isInviting: Ref<boolean>;
  inviteUsers: (users: AdminProfile[]) => Promise<boolean>;
  lastResult: Ref<AdminUserInviteResponse | null>;
}

export function useBulkUserInvite(): UseBulkUserInviteReturn {
  const toast = useToast();

  const isInviting = ref(false);
  const lastResult = ref<AdminUserInviteResponse | null>(null);

  const inviteUsers = async (users: AdminProfile[]): Promise<boolean> => {
    if (users.length === 0) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: "No users selected for invitation",
        life: 3000,
      });
      return false;
    }

    if (users.length > 50) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: "Cannot invite more than 50 users at once",
        life: 5000,
      });
      return false;
    }

    isInviting.value = true;
    lastResult.value = null;

    try {
      const inviteRequest: AdminUserInviteRequest = {
        profile_ids: users.map((user) => user.id),
      };

      const result = await adminV1InviteUsersCreate(inviteRequest);

      if (result?.results && Array.isArray(result.results)) {
        lastResult.value = result;

        const successResults = result.results.filter(
          (r: AdminUserInviteResult) => r.success
        );
        const failedResults = result.results.filter(
          (r: AdminUserInviteResult) => !r.success
        );

        const successCount = successResults.length;
        const failureCount = failedResults.length;
        const totalProcessed = result.results.length;

        if (successCount > 0 && failureCount === 0) {
          // All succeeded
          toast.add({
            severity: "success",
            summary: "Invitations Sent",
            detail: `Successfully sent invitations to ${successCount} user${successCount === 1 ? "" : "s"}`,
            life: 5000,
          });
        } else if (successCount > 0 && failureCount > 0) {
          // Partial success
          const failedNames = failedResults
            .map((failed: AdminUserInviteResult) => failed.name || failed.email)
            .join(", ");

          toast.add({
            severity: "warn",
            summary: "Partial Success",
            detail: `Sent ${successCount} invitation${successCount === 1 ? "" : "s"}, but failed for: ${failedNames}`,
            life: 10000,
          });
        } else {
          // All failed
          toast.add({
            severity: "error",
            summary: "Invitation Failed",
            detail: `Failed to send invitations to all ${totalProcessed} user${totalProcessed === 1 ? "" : "s"}`,
            life: 7000,
          });
        }

        return successCount > 0;
      } else {
        console.error("Invalid response structure:", result);
        throw new Error("Invalid response format from server");
      }
    } catch (error) {
      console.error("Bulk invite error:", error);

      const errorMessage = extractApiErrorMessage(
        error,
        "Failed to send invitations"
      );

      toast.add({
        severity: "error",
        summary: "Invitation Error",
        detail: errorMessage,
        life: 10000,
      });

      return false;
    } finally {
      isInviting.value = false;
    }
  };

  return {
    isInviting,
    inviteUsers,
    lastResult,
  };
}
